/**
 * Trial countdown banner. Rendered at the top of Settings → Plan when
 * the tenant's active subscription is in `trialing` status. Falls
 * silent on active / past_due / cancelled / suspended states.
 */

import { Card } from '@centraoneplus/ui';

interface Props {
  status: string;
  trialEndsAt?: string | null;
  fallbackPlan?: string | null;
}

export function TrialBanner({ status, trialEndsAt, fallbackPlan }: Props) {
  if (status !== 'trialing' || !trialEndsAt) return null;

  const ends = new Date(trialEndsAt);
  const now = new Date();
  const msLeft = ends.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
  const tone =
    daysLeft <= 3
      ? 'bg-red-50 text-red-900 border-red-200'
      : daysLeft <= 7
        ? 'bg-amber-50 text-amber-900 border-amber-200'
        : 'bg-blue-50 text-blue-900 border-blue-200';

  return (
    <Card className={`p-4 border ${tone}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold">
            {daysLeft === 0
              ? 'Your Professional trial ends today.'
              : `Your Professional trial has ${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining.`}
          </div>
          <p className="text-sm mt-1">
            Without action you'll drop back to the <span className="capitalize font-medium">{fallbackPlan ?? 'Free'}</span> tier on{' '}
            {ends.toLocaleDateString()}. Upgrade below to keep your workspace on Professional.
          </p>
        </div>
      </div>
    </Card>
  );
}
